import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import Text from 'design-system/Text';
import Logo from 'design-system/Logo';
import Spacer from 'design-system/Spacer';
import Button from 'design-system/Button';
import Indicator from 'design-system/Indicator';
import useColors from 'hooks/useColors';
import FXFeeRow from './FXFeeRow';
import RowAmount from './RowAmount';
import TapableText from './TapableText';
import AboutInfoRow from './AboutInfoRow';
import useStockMeta from '../hooks/useStockMeta';
import { commonToolTipStrings } from '../constants';
import useOrderPreview from '../hooks/useOrderPreview';
import { formatAmount, formatDateForLatestPrice, formatDateForLatestPriceTooltip } from '../utils';
export const orderConfirmViewStrings = {
    amount: 'Amount',
    fxConversion: 'FX conversion',
    fxConversionTooltipTitle: 'Why is this estimated?',
    fxConversionTooltipText: 'This is an estimated FX rate based on the latest data we’ve received. The FX rate you actually get may be different from the one you see here.',
    latestPrice: 'Latest price',
    latestPriceTooltipTitle: 'What is the latest price?',
    latestPriceTooltipText: 'The price shown is the market price for the instrument at ',
    numberOfShares: 'No. of shares',
    totalCost: 'Total cost',
    totalAmount: 'Total amount',
    totalAmountTooltipTitle: 'Why is this estimated?',
    totalAmountTooltipText: 'This is an estimate based on the latest price - if the price has changed at the moment you place your order, the amount will change too. We’ll always get as many shares as we can for the amount you enter, at\xa0the best available price.',
    buttonTitle: 'Confirm',
    conditions: 'Our T&C’s and Order Execution Policy apply.\nWith investment, your capital is at risk.',
    links: {
        'T&C’s': 'https://emma-app.com/trading-terms',
        'Order Execution Policy': 'https://emma-app.com/order-execution-policy',
    },
    commission: 'Commission',
};
const OrderConfirmView = (props) => {
    const isBuy = 'buy' in props;
    const isSell = 'sell' in props;
    const { symbol, name, isAlpacaAccount } = props;
    const stockCache = useStockMeta(props.assetId);
    const logoUri = stockCache?.iconUrl;
    const { cachedOrderPreview, onConfirm, assetId } = props;
    const [disableButton, setDisableButton] = useState(false);
    const [orderPreview] = useOrderPreview(assetId, props.tradingAccountId, 'buy' in props
        ? {
            operation: 'buy',
            initialAmount: props.amount.value,
            cachedOrderPreview,
        }
        : { operation: 'sell', initialAmount: props.quantity, cachedOrderPreview });
    const colors = useColors();
    const onTapConfirm = useCallback(() => {
        // just a safety check. `orderPreview` will always be defined since button is disabled until `orderPreview` is filled
        if (orderPreview) {
            onConfirm(orderPreview.order);
        }
    }, [onConfirm, orderPreview]);
    const timeoutRef = useRef(undefined);
    const handleConfirm = useCallback(() => {
        if (!disableButton) {
            setDisableButton(true);
            onTapConfirm();
            timeoutRef.current = setTimeout(() => {
                setDisableButton(false);
            }, 700);
        }
    }, [disableButton, onTapConfirm]);
    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);
    const content = useMemo(() => {
        if (!orderPreview) {
            return <Indicator dark/>;
        }
        const { estimated } = orderPreview.order;
        const { fxConversion, priceConvertedToUserCurrency, priceTimestamp } = estimated;
        const latestPriceInGBP = `(est. ${formatAmount(priceConvertedToUserCurrency)})`;
        const quantity = Math.round(estimated.quantity * 10 ** 4) / 10 ** 4;
        const totalCost = estimated.amount;
        const latestPriceTimeString = formatDateForLatestPrice(priceTimestamp);
        const latestPriceTimeStringForTooltip = formatDateForLatestPriceTooltip(priceTimestamp);
        const latestPriceRow = (<RowAmount testID="rowLatestPrice" noBottomLine noPaddingHorizontal title={orderConfirmViewStrings.latestPrice} subtitleLeft={`(as of ${latestPriceTimeString})`} subtitleRight={isAlpacaAccount ? latestPriceInGBP : undefined} amount={estimated.price} showCents est tableSpacing tooltipTitle={orderConfirmViewStrings.latestPriceTooltipTitle} tooltipText={`${orderConfirmViewStrings.latestPriceTooltipText}${latestPriceTimeStringForTooltip}`}/>);
        const fxFeeRow = fxConversion ? <FXFeeRow fxConversion={fxConversion}/> : null;
        const commissionRow = !isAlpacaAccount ? (<RowAmount showCents tableSpacing noBottomLine noPaddingHorizontal testID="rowCommission" title={orderConfirmViewStrings.commission} amount={{ currency: estimated.price.currency, value: 0 }} tooltipTitle={commonToolTipStrings.commissionTitle} tooltipText={isAlpacaAccount ? commonToolTipStrings.commissionDesc : commonToolTipStrings.commissionDescSeccl}/>) : null;
        return (<>
        {isBuy ? (<>
            {isAlpacaAccount ? (<AboutInfoRow tableSpacing title="Order type" info="Market order" noPaddingHorizontal/>) : null}
            {latestPriceRow}
            <RowAmount tableSpacing testID="rowNumberOfShares" noBottomLine noPaddingHorizontal title={orderConfirmViewStrings.numberOfShares} quantity={quantity} est tooltipTitle={commonToolTipStrings.noOfSharesTitle} tooltipText={isAlpacaAccount ? commonToolTipStrings.noOfSharesDesc : commonToolTipStrings.noOfSharesWithoutFxFeeDesc}/>
            {fxFeeRow}
            {commissionRow}
          </>) : (<>
            {isAlpacaAccount ? <AboutInfoRow title="Order type" info="Market order" noPaddingHorizontal/> : null}
            {latestPriceRow}
            <RowAmount tableSpacing testID="rowNumberOfShares" noBottomLine noPaddingHorizontal title={orderConfirmViewStrings.numberOfShares} quantity={quantity}/>
            {fxFeeRow}
            {commissionRow}
          </>)}
        <RowAmount tableSpacing testID="rowTotalCost" noPaddingHorizontal noBottomLine bold title={isBuy ? orderConfirmViewStrings.totalCost : orderConfirmViewStrings.totalAmount} amount={totalCost} showCents est tooltipTitle={isSell ? orderConfirmViewStrings.totalAmountTooltipTitle : undefined} tooltipText={isSell ? orderConfirmViewStrings.totalAmountTooltipText : undefined}/>
      </>);
    }, [isAlpacaAccount, isBuy, isSell, orderPreview]);
    return (<>
      <Spacer h={46}/>
      <View style={styles.row}>
        <View style={styles.textContainer}>
          <Text Text-20>
            {isBuy ? 'Buy' : 'Sell'} {symbol ? `$${symbol}` : name}
          </Text>
        </View>
        <Logo R44 uri={logoUri}/>
      </View>
      <Spacer h={8}/>
      <View style={styles.infoContainer}>{content}</View>
      <Spacer h={16}/>
      <TapableText TextThin-12 centered Gray pdf links={orderConfirmViewStrings.links} linkColor={colors.text.brand} text={orderConfirmViewStrings.conditions}/>
      <Spacer h={32}/>
      <Button title={orderConfirmViewStrings.buttonTitle} brand disabled={!orderPreview} onPress={handleConfirm}/>
    </>);
};
const styles = StyleSheet.create({
    infoContainer: {
        width: '100%',
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    textContainer: {
        flex: 1,
    },
});
export default OrderConfirmView;
